/* eslint-disable @typescript-eslint/no-explicit-any */
// import { toast } from "react-toastify";

import { getApiUrl } from "../config/apiConfig";

/* -------------------------------------------------------------------------- */
/*                                  TYPES                                     */
/* -------------------------------------------------------------------------- */

export interface ApiResponse<T = any> {
  value: T;
  message: string;
  status: number;
  pagination?: {
    page: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
  };
}

export class ApiError extends Error {
  status: number;
  data: any;

  constructor(status: number, message: string, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/* -------------------------------------------------------------------------- */
/*                           TOKEN CONFIGURATION                              */
/* -------------------------------------------------------------------------- */

type TokenGetter = () => string | null;
type RefreshHandler = () => Promise<string | null>;
type SessionInvalidator = () => void;

const clearPersistedSession = () => {
  if (typeof window === "undefined") return;

  localStorage.removeItem("doctor_access_token");
  localStorage.removeItem("doctor_user");
  sessionStorage.removeItem("doctor_access_token");
  sessionStorage.removeItem("doctor_user");
};

const getPersistedAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("doctor_access_token") || sessionStorage.getItem("doctor_access_token");
};

// This fallback covers requests fired before AuthProvider's effect configures
// the in-memory getter, such as a component's initial data request.
let getAccessToken: TokenGetter = getPersistedAccessToken;
let refreshAccessToken: RefreshHandler = async () => null;
let invalidateSession: SessionInvalidator = clearPersistedSession;
let sessionInvalidated = false;

/**
 * Configure once in App.tsx after login.
 */
export function configureApi(
  getToken: TokenGetter,
  refresh: RefreshHandler,
  onSessionInvalid: SessionInvalidator,
) {
  getAccessToken = getToken;
  refreshAccessToken = refresh;
  invalidateSession = onSessionInvalid;
  sessionInvalidated = false;
}

export const resetApiSession = () => {
  sessionInvalidated = false;
};

const invalidateApiSession = () => {
  if (sessionInvalidated) return;

  sessionInvalidated = true;
  clearPersistedSession();
  invalidateSession();
};

/* -------------------------------------------------------------------------- */
/*                              API FETCH                                     */
/* -------------------------------------------------------------------------- */

/**
 * Wrapper around fetch that:
 * - Adds Authorization header
 * - Supports FormData
 * - Automatically refreshes expired access token
 * - Retries the failed request only once
 */
export const apiFetch = async (
  url: string,
  options: RequestInit = {},
  retry = true,
): Promise<Response> => {
  const token = getAccessToken() || getPersistedAccessToken();

  const headers = new Headers(options.headers);

  if (!(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(getApiUrl(url), {
    ...options,
    credentials: "include",
    headers,
  });

  // Access token expired
  if (response.status === 401 && retry) {
    try {
      const freshToken = await refreshAccessToken();

      if (freshToken) {
        return apiFetch(url, options, false);
      }
    } catch (err) {
      console.error("Refresh token failed:", err);
    }
  }

  return response;
};

/* -------------------------------------------------------------------------- */
/*                           STATUS MESSAGES                                  */
/* -------------------------------------------------------------------------- */

const STATUS_MESSAGES: Record<number, string> = {
  400: "Bad request. Please check your input.",
  401: "Unauthorized. Please login again.",
  403: "Forbidden. You don't have permission.",
  404: "Resource not found.",
  409: "Conflict. Resource already exists.",
  413: "File is too large. Please use a smaller file.",
  422: "Validation error. Please check your input.",
  429: "Too many requests. Please try again later.",
  500: "Internal server error. Please try again later.",
  502: "Bad gateway. Please try again later.",
  503: "Service unavailable. Please try again later.",
};

const getStatusMessage = (status: number, fallback?: string): string => {
  return STATUS_MESSAGES[status] || fallback || "Something went wrong.";
};

/* -------------------------------------------------------------------------- */
/*                           HANDLE RESPONSE                                  */
/* -------------------------------------------------------------------------- */

export const handleResponse = async <T = any>(
  response: Response,
  context = "Request",
): Promise<ApiResponse<T>> => {
  const { status } = response;

  // No Content
  if (status === 204) {
    return {
      value: [] as any,
      message: "No data found",
      status: 204,
    };
  }

  let data: any;

  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new ApiError(status, getStatusMessage(status, `${context} failed`));
    }

    return {
      value: null as any,
      message: "Success",
      status,
    };
  }

  // Success
  if (response.ok) {
    return {
      ...data,
      status,
    };
  }

  const serverMessage = data?.message || data?.error;

  const message =
    serverMessage || getStatusMessage(status, `${context} failed`);

  console.error(`[${context}] ${status}:`, message);

  // Refresh already failed, logout user
  if (status === 401) {
    invalidateApiSession();

    // toast.error(message);

    // Optional
    // window.location.href = "/login";

    throw new ApiError(status, message, data);
  }

  //   toast.error(message);

  throw new ApiError(status, message, data);
};

/* -------------------------------------------------------------------------- */
/*                         HANDLE LIST RESPONSE                               */
/* -------------------------------------------------------------------------- */

export const handleListResponse = async <T = any>(
  response: Response,
  context = "Request",
  defaults?: {
    page?: number;
    pageSize?: number;
  },
): Promise<ApiResponse<T>> => {
  const { status } = response;

  if (status === 204) {
    return {
      value: [] as any,
      message: "No data found",
      status: 204,
      pagination: {
        page: defaults?.page || 1,
        pageSize: defaults?.pageSize || 10,
        totalRecords: 0,
        totalPages: 0,
      },
    };
  }

  let data: any;

  try {
    data = await response.json();
  } catch {
    return {
      value: [] as any,
      message: getStatusMessage(status, `${context} failed`),
      status,
      pagination: {
        page: defaults?.page || 1,
        pageSize: defaults?.pageSize || 10,
        totalRecords: 0,
        totalPages: 0,
      },
    };
  }

  if (response.ok) {
    return {
      ...data,
      status,
    };
  }

  const message =
    data?.message || getStatusMessage(status, `${context} failed`);

  console.error(`[${context}] ${status}:`, message);

  return {
    value: [] as any,
    message,
    status,
    pagination: {
      page: defaults?.page || 1,
      pageSize: defaults?.pageSize || 10,
      totalRecords: 0,
      totalPages: 0,
    },
  };
};

/* -------------------------------------------------------------------------- */
/*                              SAFE API CALL                                 */
/* -------------------------------------------------------------------------- */

export const safeApiCall = async <T>(
  fn: () => Promise<T>,
  context = "Request",
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    console.error(`[${context}] Network error:`, error);

    throw new ApiError(500, "Server error. Please try again later.");
  }
};
